import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  IndianRupee, Plus, Trash2, RefreshCw, Search, Loader2, Save, Zap, FolderOpen, X, Users, ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProjectPriceBuilder() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // New rate form — selectedResources is now an array (multi-select)
  const [newRate, setNewRate] = useState({ main_title: "", season_number: "", task_type: "", language: "", rate: "", rate_type: "per_unit" });
  const [selectedResources, setSelectedResources] = useState([]); // array of emails
  const [resources, setResources] = useState([]);
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourceDropdownOpen, setResourceDropdownOpen] = useState(false);
  const resourceDropdownRef = useRef(null);

  // Close resource dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (resourceDropdownRef.current && !resourceDropdownRef.current.contains(e.target)) setResourceDropdownOpen(false);
    };
    if (resourceDropdownOpen) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [resourceDropdownOpen]);

  const fetchRates = async () => {
    setLoading(true);
    try {
      const [ratesRes, projRes, resRes] = await Promise.all([
        axios.get(`${API}/resource-center/project-rates/unified`),
        axios.get(`${API}/resource-center/project-rates/projects`),
        axios.get(`${API}/resource-mappings`).catch(() => ({ data: { mappings: [] } })),
      ]);
      setRates(ratesRes.data.rates || []);
      setProjects(projRes.data.projects || []);
      setTaskTypes(projRes.data.task_types || []);
      setResources((resRes.data.mappings || []).map(m => ({ email: m.email, name: m.display_name || m.email.split("@")[0] })));
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRates(); }, []);

  const toggleResource = (email) => {
    setSelectedResources(prev => prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]);
  };

  const handleAddRate = async () => {
    if (!newRate.main_title || !newRate.task_type || !newRate.rate) return toast.error("Fill project, task type, and rate");
    setSaving(true);
    try {
      if (selectedResources.length > 0) {
        // Save resource-specific project rate for each selected resource (parallel)
        await Promise.all(selectedResources.map(async (email) => {
          const resRates = await axios.get(`${API}/resource-center/${encodeURIComponent(email)}`);
          const existing = resRates.data.task_rates || [];
          const updated = [...existing, {
            task_type: newRate.task_type,
            language: newRate.language || null,
            rate: parseFloat(newRate.rate),
            rate_type: newRate.rate_type,
            project: newRate.main_title,
            from_date: null, to_date: null,
          }];
          await axios.put(`${API}/resource-center/${encodeURIComponent(email)}/rates`, { rates: updated });
        }));
        toast.success(`Rate saved for ${selectedResources.length} resource${selectedResources.length>1?"s":""} on ${newRate.main_title}`);
      } else {
        await axios.post(`${API}/resource-center/project-rates/save`, {
          ...newRate,
          rate: parseFloat(newRate.rate),
          season_number: newRate.season_number || null,
          language: newRate.language || null,
        });
        toast.success("Project rate saved (applies to all resources)");
      }
      setNewRate({ main_title: "", season_number: "", task_type: "", language: "", rate: "", rate_type: "per_unit" });
      setSelectedResources([]);
      setResourceSearch("");
      fetchRates();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (rateId) => {
    try {
      await axios.delete(`${API}/resource-center/project-rates/unified/${encodeURIComponent(rateId)}`);
      toast.success("Deleted");
      fetchRates();
    } catch { toast.error("Failed"); }
  };

  const handleApplyGlobal = async () => {
    if (!window.confirm("Apply all rates globally? This uses the 4-tier cascade:\n1. Resource+Project > 2. Project Builder > 3. Individual > 4. Default")) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/resource-center/apply-rates-global`);
      toast.success(`Applied: ${res.data.updated} tasks updated`);
    } catch { toast.error("Failed"); }
    finally { setApplying(false); }
  };

  // Fetch seasons when project changes
  useEffect(() => {
    if (newRate.main_title) {
      axios.get(`${API}/resource-center/project-rates/seasons/${encodeURIComponent(newRate.main_title)}`)
        .then(res => setSeasons(res.data.seasons || []))
        .catch(() => setSeasons([]));
    } else { setSeasons([]); }
  }, [newRate.main_title]);

  const filtered = rates.filter(r => {
    if (search && !r.main_title.toLowerCase().includes(search.toLowerCase()) && !r.task_type.toLowerCase().includes(search.toLowerCase()) && !(r.resource_name || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (projectFilter && r.main_title !== projectFilter) return false;
    return true;
  });

  // Group by project
  const grouped = {};
  filtered.forEach(r => {
    const key = r.main_title;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(r);
  });

  const filteredResources = resources.filter(r => {
    if (!resourceSearch) return true;
    const q = resourceSearch.toLowerCase();
    return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
  });

  return (
    <div className="p-6" data-testid="project-price-builder">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Project Price Builder</h1>
          <p className="text-sm text-slate-500 mt-0.5">Set project-level rates that apply to ALL resources. Overrides default rates.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchRates}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApplyGlobal} disabled={applying}>
            {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
            {applying ? "Applying..." : "Apply All Rates"}
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center"><FolderOpen className="w-5 h-5 text-violet-600" /></div>
          <div><p className="text-2xl font-bold text-slate-900">{Object.keys(grouped).length}</p><p className="text-xs text-slate-500">Projects with Rates</p></div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-blue-600" /></div>
          <div><p className="text-2xl font-bold text-slate-900">{rates.length}</p><p className="text-xs text-slate-500">Total Rate Rules</p></div>
        </div>
        <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Plus className="w-5 h-5 text-emerald-600" /></div>
          <div><p className="text-2xl font-bold text-slate-900">{taskTypes.length}</p><p className="text-xs text-slate-500">Task Types</p></div>
        </div>
      </div>

      {/* Add Rate Form */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><Plus className="w-4 h-4 text-emerald-600" />Add Project Rate</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3 items-end mb-3">
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Project *</label>
              <Select value={newRate.main_title || "pick"} onValueChange={v => setNewRate(p => ({ ...p, main_title: v === "pick" ? "" : v, season_number: "" }))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent className="max-h-60">{projects.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Season</label>
              <Select value={newRate.season_number || "all"} onValueChange={v => setNewRate(p => ({ ...p, season_number: v === "all" ? "" : v }))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Seasons</SelectItem>{seasons.map(s => <SelectItem key={s} value={s}>S{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Task Type *</label>
              <Select value={newRate.task_type || "pick"} onValueChange={v => setNewRate(p => ({ ...p, task_type: v === "pick" ? "" : v }))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{taskTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div ref={resourceDropdownRef} className="relative">
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Resources <span className="text-slate-300">(blank = all)</span></label>
              <button
                type="button"
                data-testid="resource-multi-select-trigger"
                onClick={() => setResourceDropdownOpen(o => !o)}
                className="h-9 w-full flex items-center justify-between gap-2 px-3 rounded-md border border-slate-200 bg-white text-xs hover:border-slate-300 transition-colors"
              >
                <span className="flex items-center gap-1.5 truncate text-slate-700">
                  <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  {selectedResources.length === 0 ? <span className="text-slate-400">All Resources (global)</span> : <span className="font-medium">{selectedResources.length} selected</span>}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${resourceDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {resourceDropdownOpen && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
                  <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
                    <Input
                      autoFocus
                      data-testid="resource-multi-select-search"
                      placeholder="Search by name or email..."
                      className="h-8 text-xs"
                      value={resourceSearch}
                      onChange={e => setResourceSearch(e.target.value)}
                    />
                    <div className="flex items-center justify-between mt-1.5 px-1">
                      <span className="text-[10px] text-slate-400">{selectedResources.length} of {resources.length} selected</span>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setSelectedResources(filteredResources.map(r => r.email))} className="text-[10px] text-blue-600 hover:underline">Select all{resourceSearch ? " (filtered)" : ""}</button>
                        {selectedResources.length > 0 && <button type="button" onClick={() => setSelectedResources([])} className="text-[10px] text-slate-500 hover:underline">Clear</button>}
                      </div>
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {filteredResources.length === 0 ? (
                      <p className="text-xs text-slate-400 py-4 text-center">No resources match</p>
                    ) : filteredResources.map(r => {
                      const checked = selectedResources.includes(r.email);
                      return (
                        <label key={r.email} data-testid={`resource-option-${r.email}`} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs">
                          <input type="checkbox" checked={checked} onChange={() => toggleResource(r.email)} className="w-3.5 h-3.5 accent-emerald-600" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 truncate">{r.name}</div>
                            <div className="text-[10px] text-slate-400 truncate">{r.email}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {selectedResources.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {selectedResources.slice(0, 4).map(email => {
                    const r = resources.find(x => x.email === email);
                    return (
                      <Badge key={email} className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] gap-1 pr-1">
                        {r?.name || email}
                        <button onClick={() => toggleResource(email)} className="hover:bg-blue-100 rounded p-0.5"><X className="w-2.5 h-2.5" /></button>
                      </Badge>
                    );
                  })}
                  {selectedResources.length > 4 && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">+{selectedResources.length - 4} more</Badge>}
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Rate ({"\u20B9"}) *</label>
              <Input type="number" value={newRate.rate} onChange={e => setNewRate(p => ({ ...p, rate: e.target.value }))} placeholder="0.00" className="h-9 text-xs" />
            </div>
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Rate Type</label>
              <Select value={newRate.rate_type} onValueChange={v => setNewRate(p => ({ ...p, rate_type: v }))}>
                <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="per_unit">Per Unit</SelectItem><SelectItem value="flat">Flat</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[9px] font-medium text-slate-500 uppercase mb-1 block">Language <span className="text-slate-300">(blank = all)</span></label>
              <Input value={newRate.language} onChange={e => setNewRate(p => ({ ...p, language: e.target.value }))} placeholder="All" className="h-9 text-xs" />
            </div>
            <div>
              <Button data-testid="add-rate-btn" className="h-9 w-full bg-emerald-600 hover:bg-emerald-700 text-sm" onClick={handleAddRate} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                {selectedResources.length > 0 ? `Add for ${selectedResources.length} Resource${selectedResources.length>1?"s":""}` : "Add Global Rate"}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100">
            <p className="text-[10px] text-slate-400">Priority: <span className="font-medium text-slate-500">Resource+Project</span> &gt; <span className="font-medium text-violet-500">Project Builder</span> &gt; <span className="font-medium text-slate-500">Individual rate</span> &gt; <span className="font-medium text-slate-500">Default</span></p>
            {selectedResources.length > 0 && <Badge className="bg-blue-50 text-blue-700 border-0 text-[10px]">Resource-specific · {selectedResources.length} selected</Badge>}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="bg-white rounded-xl border p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input className="h-8 pl-8 text-[12px]" placeholder="Search project or task..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={projectFilter || "all"} onValueChange={v => setProjectFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-8 w-48 text-[12px]"><SelectValue placeholder="All Projects" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Projects</SelectItem>{[...new Set(rates.map(r => r.main_title))].sort().map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400 ml-auto">{filtered.length} rates</span>
      </div>

      {/* Rates grouped by project */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-violet-400" /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm">No project rates configured yet</p>
          <p className="text-xs text-slate-300 mt-1">Add your first project rate above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([project, projectRates]) => (
            <Card key={project}>
              <CardContent className="p-0">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
                  <FolderOpen className="w-4 h-4 text-violet-500" />
                  <span className="text-sm font-semibold text-slate-800">{project}</span>
                  <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">{projectRates.length} rules</Badge>
                </div>
                <div className="divide-y divide-slate-50">
                  {projectRates.map((r, i) => (
                    <div key={r.id || i} data-testid={`rate-row-${r.id}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/50">
                      {r.scope === "resource" ? (
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] gap-1"><Users className="w-2.5 h-2.5" />{r.resource_name}</Badge>
                      ) : (
                        <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px]">All Resources</Badge>
                      )}
                      {r.season_number && <Badge className="bg-violet-100 text-violet-700 border-0 text-[10px]">S{r.season_number}</Badge>}
                      <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{r.task_type}</Badge>
                      {r.language && <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{r.language}</Badge>}
                      {!r.language && <span className="text-[10px] text-slate-400">All Langs</span>}
                      <span className="ml-auto font-mono text-sm font-bold text-emerald-700">{"\u20B9"}{r.rate}</span>
                      <Badge className="bg-slate-50 text-slate-500 border-0 text-[9px]">{r.rate_type}</Badge>
                      <button onClick={() => handleDelete(r.id)} className="p-1 text-slate-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
